import { tokenStorage } from '@/domains/auth/storage';
import { API_BASE_URL } from '@/runtime-config';
import { ApiError, type IProblemDetails } from './problem';

/**
 * The one fetch wrapper for survey-api.
 *
 * Everything the app sends goes through here so three things are decided once:
 * the base URL, the bearer header (survey-api's sessions are opaque tokens, not
 * JWTs — the server holds the state), and turning a ProblemDetails body into an
 * `ApiError` a caller can switch on.
 *
 * Two ways in share one `send()`: `apiRequest` answers the body alone, which is what every
 * read and write wanted until the advisor record page, and `apiRequestWithEtag` answers the
 * body with the response's `ETag` beside it, for the routes whose precondition is quoted
 * back from a header rather than from the body. A second function rather than a changed
 * return type, so the hundred-odd callers that want the body alone are not touched.
 *
 * A 401 on a signed-in call drops the session through one handler (below).
 */

export interface IRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /**
   * JSON-encoded, except a `Blob` (a `File` included), which goes as its bytes — the photo
   * upload's raw body. Its `Content-Type` is the caller's header if one is given, else the
   * blob's own `type`, else `application/octet-stream`; survey-api judges the type (415) and
   * this side does not. A `FormData` — the list import's upload, the one multipart route — goes
   * as it is, with no `Content-Type` of ours: the browser writes the header itself, boundary
   * included, and a header set here would carry no boundary and could not be read.
   */
  body?: unknown;
  signal?: AbortSignal;
  /** Send no Authorization header — the IdP exchange and the mailed links' redemptions. */
  anonymous?: boolean;
  /**
   * Extra request headers.
   *
   * Exists for `If-Match`, survey-api's precondition on the authoring drafts, the staff
   * corrections, a work item's `PATCH` and reopening and the refresh schedule's `PUT` — a
   * missing header is 428, a stale one 409 — which survey-api made **required** rather than
   * optional, on the grounds that an optional precondition is one a client forgets exactly
   * once and then loses an edit silently.
   *
   * There are two ways to hold the value it quotes. The authoring reads carry `revision` in
   * their own body, so `definitions-api.ts` builds the header from that, through
   * `apiRequest`. The others are read through `apiRequestWithEtag`, and the `ETag` it hands
   * back is quoted as received: it is opaque here, never parsed into a number, so nothing on
   * this side depends on how survey-api spells a revision.
   */
  headers?: Record<string, string>;
}

/** Both refusal handlers below take nothing and answer nothing; what differs is the status
 *  that reaches them and what the provider does about it. */
type RefusalHandler = () => void;

let onUnauthorized: Nullable<RefusalHandler> = null;
let onForbidden: Nullable<RefusalHandler> = null;

/**
 * Registered by the auth provider. A 401 from any endpoint means the session
 * the server holds is gone (expired, revoked, purged), and the only correct
 * reaction is to drop it locally too — every caller would otherwise have to
 * re-implement that.
 */
export function setUnauthorizedHandler(handler: Nullable<RefusalHandler>) {
  onUnauthorized = handler;
}

/**
 * Registered by the auth provider as well, and deliberately **not** the same kind of
 * handler as the one above: a 401 is a fact, a 403 is a question.
 *
 * survey-api answers most refusals with an empty body (AUTH-§11.2), so the status alone does
 * not say whether the account's permissions changed since `/auth/me` was last read — an
 * administrator can take one away at any moment. Only the provider can tell, and only by
 * asking.
 *
 * So this notifies rather than reacts: the error is thrown to the caller exactly as before,
 * and whatever the provider decides happens beside it.
 */
export function setForbiddenHandler(handler: Nullable<RefusalHandler>) {
  onForbidden = handler;
}

async function readProblem(response: Response): Promise<Nullable<IProblemDetails>> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) return null;

  try {
    return (await response.json()) as IProblemDetails;
  } catch {
    return null;
  }
}

/** A body and the response's `ETag`, verbatim — `null` where the response carries none. */
export interface IEtagged<T> {
  body: T;
  etag: Nullable<string>;
}

function contentTypeOf(body: unknown): Nullable<string> {
  if (body === undefined || body instanceof FormData) return null;
  if (body instanceof Blob) return body.type || 'application/octet-stream';
  return 'application/json';
}

/** What `fetch` is handed: a blob or a form as it is, anything else as JSON. */
function sendable(body: unknown): BodyInit {
  return body instanceof Blob || body instanceof FormData ? body : JSON.stringify(body);
}

async function send<T>(
  path: string,
  options: IRequestOptions,
  read: 'json' | 'blob' = 'json',
  onHeaders?: (headers: Headers) => void,
  onProgress?: (received: number) => void
): Promise<IEtagged<T>> {
  const { method = 'GET', body, signal, anonymous, headers } = options;
  const token = anonymous ? null : tokenStorage.get();
  // A caller's own `Content-Type`, in any spelling, replaces ours rather than joining it: two keys
  // differing in case would both be sent, as one combined value.
  const callerType = Object.keys(headers ?? {}).some(
    (name) => name.toLowerCase() === 'content-type'
  );
  const contentType = callerType ? null : contentTypeOf(body);

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    signal,
    headers: {
      Accept: 'application/json',
      ...(contentType ? { 'Content-Type': contentType } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // Last, so a caller cannot accidentally drop the bearer: the headers callers send are
      // `If-Match` and a blob's `Content-Type`, and an override of `Authorization` would be a
      // bug that looks like a feature.
      ...headers
    },
    ...(body === undefined ? {} : { body: sendable(body) })
  });

  if (!response.ok) {
    const problem = await readProblem(response);

    // A 401 on an authenticated call is a dead session, not a call-site error.
    // The IdP exchange answers 401 too, but it is `anonymous` and must surface as a
    // refused sign-in rather than as a session drop.
    if (response.status === 401 && !anonymous) onUnauthorized?.();

    // Never for an anonymous call: its 403 — the exchange's `account-not-linked` — is about the
    // identity it carried, and a session the request did not use is not evidence about the
    // session this browser holds.
    if (response.status === 403 && !anonymous) onForbidden?.();

    throw new ApiError(
      response.status,
      problem,
      problem?.title ?? `Request failed with status ${response.status}`
    );
  }

  const etag = response.headers.get('ETag');
  onHeaders?.(response.headers);
  if (read === 'blob') return { body: (await readBlob(response, onProgress)) as T, etag };
  if (response.status === 204 || response.status === 205) return { body: null as T, etag };

  const text = await response.text();
  return { body: (text ? JSON.parse(text) : null) as T, etag };
}

/** The body as one blob — read as a stream and counted chunk by chunk where a caller asks how far
 *  it has got, the same bytes either way. An abort mid-body rejects the read like the fetch. */
async function readBlob(response: Response, onProgress?: (received: number) => void): Promise<Blob> {
  if (!onProgress || !response.body) return response.blob();
  const reader = response.body.getReader();
  const chunks: BlobPart[] = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.byteLength;
    onProgress(received);
  }
  return new Blob(chunks, { type: response.headers.get('Content-Type') ?? '' });
}

/**
 * Returns null for 204/205 and for an empty body — several endpoints
 * (`/auth/logout`, the cursor write) answer with no content by design.
 */
export async function apiRequest<T>(path: string, options: IRequestOptions = {}): Promise<T> {
  return (await send<T>(path, options)).body;
}

/**
 * `apiRequest`, with the response's `ETag` handed back beside the body for a caller to quote
 * as `If-Match` on its next write. survey-api's CORS policy exposes the header
 * (`WithExposedHeaders`), without which a cross-origin read would see `null`.
 */
export function apiRequestWithEtag<T>(
  path: string,
  options: IRequestOptions = {}
): Promise<IEtagged<T>> {
  return send<T>(path, options);
}

/** A file survey-api wrote, and the name its `Content-Disposition` gives — null where it gives
 *  none, or the header is not exposed to this origin. */
export interface IDownload {
  file: Blob;
  fileName: Nullable<string>;
}

/** What a download's caller may be told before it settles. */
export interface IDownloadOptions extends IRequestOptions {
  /** At the first byte, before the body is read: the file's name and the response's headers. */
  onStart?: (start: { fileName: Nullable<string>; headers: Headers }) => void;
  /** The bytes received so far, after each chunk. */
  onProgress?: (received: number) => void;
}

/**
 * A file survey-api writes — the ranking scoring input, the finalists' workbooks, a list's export —
 * as the bytes it sent, through the same bearer and refusal handling as every other call: a refusal
 * is still an `ApiError` from its ProblemDetails. The name is read from `Content-Disposition`
 * (`filename*` first, then `filename`); a caller keeps a name of its own for when there is none.
 *
 * **A long file can be watched as it comes** (`onStart`, `onProgress`): a list's export streams for
 * many seconds with no `Content-Length`, so its caller is told the headers at the first byte and the
 * bytes counted since. The file is still handed over whole, as one `Blob`, once the body has ended:
 * a browser's save takes a file, not a stream.
 */
export async function apiDownload(path: string, options: IDownloadOptions = {}): Promise<IDownload> {
  const { onStart, onProgress, ...request } = options;
  let fileName: Nullable<string> = null;
  const answer = await send<Blob>(
    path,
    { ...request, headers: { Accept: '*/*', ...request.headers } },
    'blob',
    (headers) => {
      fileName = dispositionName(headers.get('Content-Disposition'));
      onStart?.({ fileName, headers });
    },
    onProgress
  );
  return { file: answer.body, fileName };
}

/** The file name a `Content-Disposition` value carries, RFC 6266's way. */
function dispositionName(value: Nullable<string>): Nullable<string> {
  if (!value) return null;
  const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(value);
  if (extended) {
    try {
      return decodeURIComponent(extended[2].trim());
    } catch {
      // A malformed escape falls back to the plain parameter.
    }
  }
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(value);
  return plain ? (plain[2] ?? plain[1]).trim() : null;
}

/** GET that treats 404 as "nothing there", the shape several reads want. */
export async function apiGetOrNull<T>(
  path: string,
  options: IRequestOptions = {}
): Promise<Nullable<T>> {
  try {
    return await apiRequest<T>(path, options);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}
